import { newIdempotencyKey } from "@station/api-client";
import {
  MARKETING_CHANNELS,
  MARKETING_MESSAGE_STATUS_LABELS,
  MARKETING_MESSAGE_STATUSES,
  type MarketingBatchResult,
  type MarketingMessage,
  marketingMessageStatusSchema,
  MESSAGE_CHANNEL_LABELS,
} from "@station/contracts";
import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Megaphone, Send } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Section } from "../../../features/expenses/shared.js";
import {
  CustomerCombobox,
  type CustomerOption,
  SelectedCustomers,
} from "../../../features/privacy/customer-picker.js";
import { type FieldMessages, translateErrors } from "../../../features/privacy/form-errors.js";
import {
  batchSummary,
  MARKETING_BODY_MAX,
  marketingBody,
  type MarketingChannel,
  type MarketingFormField,
  type MarketingFormValues,
  type RecipientMode,
  validateMarketingForm,
} from "../../../features/privacy/privacy-values.js";
import {
  type MarketingLogFilters,
  useMarketingMessages,
  usePrivacyPermissions,
  useRefreshPrivacy,
  useStationOptions,
} from "../../../features/privacy/queries.js";
import { MarketingStatusBadge, PrivacyPage } from "../../../features/privacy/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  status: marketingMessageStatusSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kvkk/pazarlama")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MarketingRoute,
});

/**
 * Pazarlama iletisi (ADR 0017 §8) — TEK gönderim yolu: her alıcının ileti izni gönderim ANINDA defterden denetlenir;
 * izinsiz, iletişimsiz ya da anonim alıcıya GİTMEZ ve günlüğe gerekçesiyle yazılır. Gönderim `marketing.send`
 * (hız sınırı dakikada 10), günlük `privacy.read`. İleti metni günlükte yalnızca başıyla tutulur; kişisel veri yok.
 */
function MarketingRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePrivacyPermissions();
  const [lastBatch, setLastBatch] = useState<MarketingBatchResult | null>(null);
  return (
    <PrivacyPage
      title={t("menu.marketingMessages")}
      description={t("privacy.marketing.description")}
      permissions={["marketing.send", "privacy.read"]}
    >
      {permissions.canSend ? <SendSection onSent={setLastBatch} /> : null}
      {lastBatch ? <BatchResultSection result={lastBatch} canRead={permissions.canRead} /> : null}
      {permissions.canRead ? <MessageLog /> : null}
    </PrivacyPage>
  );
}

function emptyForm(mode: RecipientMode): MarketingFormValues {
  return { channel: "SMS", subject: "", body: "", mode, customerIds: [], homeStationId: null };
}

function SendSection({ onSent }: { onSent: (result: MarketingBatchResult) => void }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPrivacy();
  const permissions = usePrivacyPermissions();
  const stations = useStationOptions();
  const [values, setValues] = useState<MarketingFormValues>(() =>
    emptyForm(permissions.canPickCustomers ? "CUSTOMERS" : "SEGMENT"),
  );
  const [selected, setSelected] = useState<CustomerOption[]>([]);
  const [errors, setErrors] = useState<FieldMessages<MarketingFormField>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const patch = (next: Partial<MarketingFormValues>) => setValues((current) => ({ ...current, ...next }));
  const current: MarketingFormValues = { ...values, customerIds: selected.map((customer) => customer.id) };
  const stationOptions = stations.data ?? [];
  const station = stationOptions.find((item) => item.id === values.homeStationId) ?? null;

  const send = useMutation({
    mutationFn: async (form: MarketingFormValues) =>
      (
        await api.POST("/marketing/messages", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body: marketingBody(form),
        })
      ).data!,
    onSuccess: async (result) => {
      onSent(result);
      setSelected([]);
      setValues((previous) => ({ ...emptyForm(previous.mode), channel: previous.channel }));
      await refresh();
      toast({ tone: "success", title: t("privacy.marketing.sent", { total: formatCount(result.total) }) });
    },
  });

  return (
    <Section title={t("privacy.marketing.formTitle")}>
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          const found = validateMarketingForm(current);
          setErrors(translateErrors(found, t));
          if (Object.keys(found).length === 0) setConfirming(true);
        }}
      >
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">{t("privacy.marketing.fields.channel")}</legend>
          <div className="flex flex-wrap gap-4">
            {MARKETING_CHANNELS.map((channel) => (
              <label key={channel} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="marketingChannel"
                  className="size-4 accent-[var(--primary)]"
                  checked={values.channel === channel}
                  onChange={() => patch({ channel: channel as MarketingChannel })}
                />
                {MESSAGE_CHANNEL_LABELS[channel]}
              </label>
            ))}
          </div>
        </fieldset>

        {values.channel === "EMAIL" ? (
          <FormField label={t("privacy.marketing.fields.subject")} error={errors.subject} required>
            {(control) => (
              <Input
                {...control}
                maxLength={200}
                value={values.subject}
                onChange={(event) => patch({ subject: event.target.value })}
              />
            )}
          </FormField>
        ) : null}

        <FormField
          label={t("privacy.marketing.fields.body")}
          description={
            values.channel === "SMS"
              ? t("privacy.marketing.fields.smsCounter", {
                  count: formatCount(values.body.length),
                  max: formatCount(MARKETING_BODY_MAX),
                })
              : t("privacy.marketing.fields.bodyHint", { max: formatCount(MARKETING_BODY_MAX) })
          }
          error={errors.body}
          required
        >
          {(control) => (
            <Textarea
              {...control}
              rows={5}
              maxLength={MARKETING_BODY_MAX}
              value={values.body}
              onChange={(event) => patch({ body: event.target.value })}
            />
          )}
        </FormField>

        <fieldset className="grid gap-3">
          <legend className="mb-1 text-sm font-medium">{t("privacy.marketing.fields.recipients")}</legend>
          <div className="flex flex-wrap gap-4">
            {permissions.canPickCustomers ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="marketingRecipients"
                  className="size-4 accent-[var(--primary)]"
                  checked={values.mode === "CUSTOMERS"}
                  onChange={() => patch({ mode: "CUSTOMERS" })}
                />
                {t("privacy.marketing.modes.CUSTOMERS")}
              </label>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="marketingRecipients"
                className="size-4 accent-[var(--primary)]"
                checked={values.mode === "SEGMENT"}
                onChange={() => patch({ mode: "SEGMENT" })}
              />
              {t("privacy.marketing.modes.SEGMENT")}
            </label>
          </div>
          {values.mode === "CUSTOMERS" ? (
            <div className="grid gap-2">
              <FormField
                label={t("privacy.marketing.fields.addCustomer")}
                description={t("privacy.marketing.fields.selectedCount", {
                  count: formatCount(selected.length),
                })}
                error={errors.recipients}
                className="max-w-xl"
              >
                {(control) => (
                  <CustomerCombobox
                    {...control}
                    value={null}
                    clearOnSelect
                    excludeIds={selected.map((customer) => customer.id)}
                    onChange={(customer) => {
                      if (customer) setSelected((all) => [...all, customer]);
                    }}
                  />
                )}
              </FormField>
              <SelectedCustomers
                customers={selected}
                onRemove={(id) => setSelected((all) => all.filter((customer) => customer.id !== id))}
              />
            </div>
          ) : stationOptions.length <= 1 ? (
            <p className="text-sm text-muted-foreground">{t("privacy.marketing.fields.segmentHint")}</p>
          ) : (
            <FormField
              label={t("privacy.marketing.fields.homeStation")}
              description={t("privacy.marketing.fields.segmentHint")}
              className="max-w-md"
            >
              {(control) => (
                <Select
                  {...control}
                  value={values.homeStationId ?? ""}
                  onChange={(event) => patch({ homeStationId: event.target.value || null })}
                >
                  <option value="">{t("privacy.marketing.fields.allStations")}</option>
                  {stationOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.code} {option.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          )}
        </fieldset>

        <Alert tone="info">{t("privacy.marketing.permissionNote")}</Alert>
        <div className="flex justify-end">
          <Button type="submit" loading={send.isPending}>
            <Send aria-hidden="true" />
            {t("privacy.marketing.send")}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("privacy.marketing.confirmTitle")}
        description={t("privacy.marketing.confirmDescription")}
        confirmLabel={t("privacy.marketing.confirmSend")}
        onConfirm={async () => {
          try {
            await send.mutateAsync(current);
          } catch (caught) {
            setFormError(
              isApiError(caught, "RATE_LIMITED") ? t("privacy.marketing.rateLimited") : errorMessage(caught),
            );
          }
        }}
      >
        <dl className="grid gap-1 rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <dt className="text-muted-foreground">{t("privacy.marketing.fields.channel")}:</dt>
            <dd className="font-medium">{MESSAGE_CHANNEL_LABELS[values.channel]}</dd>
          </div>
          <div className="flex flex-wrap gap-2">
            <dt className="text-muted-foreground">{t("privacy.marketing.fields.recipients")}:</dt>
            <dd className="font-medium">
              {values.mode === "CUSTOMERS"
                ? t("privacy.marketing.recipientsCustomers", { count: formatCount(selected.length) })
                : station
                  ? t("privacy.marketing.recipientsSegmentStation", {
                      station: `${station.code} ${station.name}`,
                    })
                  : t("privacy.marketing.recipientsSegment")}
            </dd>
          </div>
        </dl>
      </ConfirmDialog>
    </Section>
  );
}

/** Gönderim sonucu: sayılar + rozetler (anlam METİNLE) ve o partinin günlüğü. */
function BatchResultSection({
  result,
  canRead,
}: {
  result: MarketingBatchResult;
  canRead: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const summary = batchSummary(result);
  return (
    <Section title={t("privacy.marketing.resultTitle")}>
      <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="marketing-batch-summary">
        <div className="grid gap-1 rounded-md border p-3" data-status="TOTAL">
          <dt className="text-xs text-muted-foreground">{t("common.total")}</dt>
          <dd className="text-lg font-semibold tabular">{formatCount(summary.total)}</dd>
        </div>
        {summary.items.map((item) => (
          <div key={item.status} className="grid gap-1 rounded-md border p-3" data-status={item.status}>
            <dt>
              <StatusBadge tone={item.tone}>{t(`privacy.marketing.summary.${item.status}`)}</StatusBadge>
            </dt>
            <dd className="text-lg font-semibold tabular">{formatCount(item.count)}</dd>
          </div>
        ))}
      </dl>
      {summary.consistent ? null : <Alert tone="warning">{t("privacy.marketing.inconsistent")}</Alert>}
      {canRead ? (
        <MessageTable filters={{ batchId: result.batchId }} caption={t("privacy.marketing.batchCaption")} />
      ) : (
        <p className="text-sm text-muted-foreground">{t("privacy.marketing.noLogPermission")}</p>
      )}
    </Section>
  );
}

function MessageLog(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  return (
    <Section title={t("privacy.marketing.logTitle")}>
      <MessageTable
        filters={{ status: search.status }}
        caption={t("privacy.marketing.logCaption")}
        statusFilter={{
          value: search.status,
          onChange: (status) =>
            void navigate({
              to: ".",
              search: ((previous: Record<string, unknown>) =>
                compactSearch({ ...previous, status })) as never,
              replace: true,
            }),
        }}
      />
    </Section>
  );
}

function MessageTable({
  filters,
  caption,
  statusFilter,
}: {
  filters: MarketingLogFilters;
  caption: string;
  statusFilter?: { value: string | undefined; onChange: (status: string | undefined) => void };
}): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useMarketingMessages(filters, pages.cursor);
  const columns = useMemo<DataTableColumn<MarketingMessage>[]>(
    () => [
      {
        id: "createdAt",
        header: t("privacy.marketing.fields.createdAt"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "customer",
        header: t("privacy.marketing.fields.customer"),
        hideable: false,
        cell: (row) => <span className="font-mono whitespace-nowrap">{row.customerCode ?? "—"}</span>,
      },
      {
        id: "channel",
        header: t("privacy.marketing.fields.channel"),
        cell: (row) => MESSAGE_CHANNEL_LABELS[row.channel],
      },
      {
        id: "content",
        header: t("privacy.marketing.fields.content"),
        cell: (row) => (
          <span className="grid min-w-56 max-w-96 gap-0.5">
            {row.subject ? <span className="font-medium">{row.subject}</span> : null}
            <span className="line-clamp-2 text-muted-foreground">{row.bodyPreview}</span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => <MarketingStatusBadge status={row.status} />,
      },
      {
        id: "reason",
        header: t("privacy.marketing.fields.reason"),
        // Atlanan (SKIPPED_*) ve hatalı satırda sunucu Türkçe gerekçeyi HER ZAMAN doldurur; gönderilende boştur.
        exportValue: (row) => (row.status === "SENT" ? null : row.reason),
        cell: (row) =>
          row.status === "SENT" || !row.reason ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span
              className={
                row.status === "FAILED" ? "block min-w-40 text-sm text-destructive" : "block min-w-40 text-sm"
              }
            >
              {row.reason}
            </span>
          ),
      },
      {
        id: "providerRef",
        header: t("privacy.marketing.fields.providerRef"),
        defaultVisible: false,
        cell: (row) => <span className="font-mono text-xs">{row.providerRef ?? "—"}</span>,
      },
    ],
    [t],
  );
  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          icon={<Megaphone />}
          title={t("privacy.marketing.emptyTitle")}
          description={t("privacy.marketing.emptyDescription")}
        />
      }
      filters={
        statusFilter ? (
          <FormField label={t("common.status")} className="w-64">
            {(control) => (
              <Select
                {...control}
                value={statusFilter.value ?? ""}
                onChange={(event) => statusFilter.onChange(event.target.value || undefined)}
              >
                <option value="">{t("common.all")}</option>
                {MARKETING_MESSAGE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {MARKETING_MESSAGE_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : undefined
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
